import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Documentation from './pages/Documentation';
import Support from './pages/Support';
import PromptDetail from './pages/PromptDetail';
import ProfilePage from './pages/ProfilePage';
import SharedPage from './pages/SharedPage';
import CoursesPage from './pages/CoursesPage';
import SettingsPage from './pages/SettingsPage';
import InviteAcceptPage from './pages/InviteAcceptPage';
import SavedPromptsPage from './pages/SavedPromptsPage';
import FavoritePromptsPage from './pages/FavoritePromptsPage';
import PromptLibraryPage from './pages/PromptLibraryPage';
import TrashPage from './pages/TrashPage';
import TeamPage from './pages/TeamPage';
import ApiPage from './pages/ApiPage';
import HelpPage from './pages/HelpPage';
import AdminPage from './pages/AdminPage';
import AgentInstructionsPage from './pages/AgentInstructionsPage';
import { InstructionProjectsIndexView, InstructionProjectFoldersView, InstructionFolderInstructionsView } from './pages/AgentInstructionWorkspacePage';
import InviteSignupPage from './pages/InviteSignupPage';
import WelcomePage from './pages/WelcomePage';
import PricingPage from './pages/PricingPage';
import PublicInstructionPage from './pages/PublicInstructionPage';
import AppShell from './components/AppShell';
import SelectMenu from './components/SelectMenu';
import Modal from './components/Modal';
import ColorPicker from './components/ColorPicker';
import { ProjectsIndexView, ProjectFoldersView, FolderPromptsView } from './pages/ProjectsPage';
import { apiClient, clearAuthToken } from './apiClient';
import AuthContext from './context/AuthContext';
import { SocialHubHome, SocialGuildDirectory, SocialGuildPage, SocialPostDetail, SocialNotifications } from './pages/SocialHub';
import InboxPage from './pages/InboxPage';
import RichTextInput from './components/RichTextInput';
import { sanitizeRichText } from './utils/richText';
import ErrorBoundary from './components/ErrorBoundary';
import { SKOOL_SIGNUP_URL } from './config/externalLinks';

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    try {
      const userData = await apiClient.get('/api/auth/session');
      if (!userData?.user_id) {
        clearAuthToken();
        setUser(null);
        return;
      }
      let permissionsData = null;
      try {
        permissionsData = await apiClient.get('/api/access/permissions');
      } catch (permError) {
        console.warn('Permissions fetch failed:', permError);
      }
      const mergedUser = {
        ...userData,
        permissions: permissionsData?.permissions || userData?.permissions,
        account_level: permissionsData?.level || userData?.account_tier || userData?.account_level
      };
      setUser(mergedUser);
    } catch (error) {
      clearAuthToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const logout = async () => {
    try {
      await apiClient.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      clearAuthToken();
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function hasAdminAccess(user) {
  if (!user) return false;
  const accountLevel = String(user.account_level || user.account_tier || user.role || '').trim().toLowerCase();
  return Boolean(user.is_admin) || accountLevel === 'admin';
}

// ==================== PROTECTED ROUTE ====================
function ProtectedRoute({ children }) {
  const { user, loading, logout } = React.useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return <Navigate to="/login" replace />;
  }

  return <AppShell onLogout={logout} user={user}>{children}</AppShell>;
}

function AdminRoute({ children }) {
  const { user, loading, logout } = React.useContext(AuthContext);
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user && !location.state?.user) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAdminAccess(user)) {
    return <Navigate to="/profile" replace />;
  }

  return <AppShell onLogout={logout} user={user}>{children}</AppShell>;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  return null;
}

function ExternalRedirectPage({ href }) {
  useEffect(() => {
    window.location.replace(href);
  }, [href]);

  return (
    <div className="min-h-screen flex items-center justify-center px-4 text-center">
      <div>
        <p className="text-gray-300 mb-4">Redirecting you to the member signup page...</p>
        <a href={href} target="_blank" rel="noreferrer" className="text-rebel-blue hover:underline">
          Continue if you are not redirected automatically
        </a>
      </div>
    </div>
  );
}

// ==================== CREATE PROMPT ====================
function CreatePrompt() {
  const { promptId } = useParams();
  const isEdit = Boolean(promptId);
  const { user } = React.useContext(AuthContext);
  const location = useLocation();
  const forkSource = location.state?.forkSource || null;
  const forkCreator = location.state?.forkCreator || null;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [template, setTemplate] = useState('');
  const templateRef = useRef(null);
  const [categoryId, setCategoryId] = useState('');
  const [visibility, setVisibility] = useState('private');
  const projectLabel = location.state?.projectName || null;
  const folderLabel = location.state?.folderName || null;
  const [selectedProjectId, setSelectedProjectId] = useState(location.state?.projectId || '');
  const [selectedFolderId, setSelectedFolderId] = useState(location.state?.folderId || '');
  const [projects, setProjects] = useState([]);
  const [folders, setFolders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [variables, setVariables] = useState([]);
  const [step, setStep] = useState(1);
  const [stepError, setStepError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    project: false,
    folder: false,
    category: false
  });
  const [loading, setLoading] = useState(false);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [editError, setEditError] = useState('');
  const [promptOwnerId, setPromptOwnerId] = useState(null);
  const pendingFolderRef = useRef(null);
  const [highlightedVariable, setHighlightedVariable] = useState(null);
  const [lockedHighlight, setLockedHighlight] = useState(null);
  const selectionRef = useRef({ start: null, end: null });
  const [forkInfo, setForkInfo] = useState(forkSource ? { prompt: forkSource, creator: forkCreator } : null);
  const [forkedFromPromptId, setForkedFromPromptId] = useState(forkSource?.prompt_id || null);
  const forkInitRef = useRef(false);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [creatingProject, setCreatingProject] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [projectForm, setProjectForm] = useState({
    name: '',
    description: '',
    category_id: '',
    color: '#3B82F6',
    visibility: 'private'
  });
  const [folderForm, setFolderForm] = useState({
    name: '',
    description: '',
    category_id: '',
    color: '#3B82F6',
    visibility: 'private',
    project_id: ''
  });
  const navigate = useNavigate();

  const sortedCategories = useMemo(
    () => [...(categories || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [categories]
  );

  const formatDisplayName = (value) => {
    return String(value || '')
      .replace(/_/g, ' ')
      .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const normalizeVariableName = (value) => {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  };

  useEffect(() => {
    fetchCategories();
    fetchProjects();
  }, []);

  useEffect(() => {
    if (isEdit || !forkSource || forkInitRef.current) return;
    forkInitRef.current = true;
    setForkInfo({ prompt: forkSource, creator: forkCreator });
    setForkedFromPromptId(forkSource.prompt_id || null);
    setName(forkSource.name || '');
    setDescription(forkSource.description || '');
    setTemplate(forkSource.prompt_template || '');
    setCategoryId(forkSource.category_id || '');
    setVisibility('private');
    const loadedVariables = (forkSource.variables || []).map((variable) => ({
      variable_name: variable.variable_name,
      display_name: variable.display_name || formatDisplayName(variable.variable_name),
      description: variable.description || '',
      input_type: variable.input_type || 'text',
      is_required: variable.is_required ?? true,
      default_value: variable.default_value || '',
      dropdown_options: variable.dropdown_options || []
    }));
    if (loadedVariables.length) {
      setVariables(loadedVariables);
    }
  }, [forkSource, forkCreator, isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    let mounted = true;

    const loadPrompt = async () => {
      setLoadingPrompt(true);
      setEditError('');
      try {
        const data = await apiClient.get(`/api/prompts/${promptId}`);
        if (!mounted) return;

        setPromptOwnerId(data.user_id);
        if (user?.user_id && data.user_id !== user.user_id) {
          setEditError('You can only edit prompts that you created.');
        }
        setForkedFromPromptId(data.forked_from_prompt_id || null);

        const loadedVariables = (data.variables || []).map((variable) => ({
          variable_name: variable.variable_name,
          display_name: variable.display_name || formatDisplayName(variable.variable_name),
          description: variable.description || '',
          input_type: variable.input_type || 'text',
          is_required: variable.is_required ?? true,
          default_value: variable.default_value || '',
          dropdown_options: variable.dropdown_options || []
        }));

        setVariables(loadedVariables);
        setName(data.name || '');
        setDescription(data.description || '');
        setTemplate(data.prompt_template || '');
        setCategoryId(data.category_id || '');
        setVisibility(data.visibility || 'private');

        pendingFolderRef.current = {
          projectId: data.project_id || '',
          folderId: data.folder_id || ''
        };

        setSelectedProjectId(data.project_id || '');
        setSelectedFolderId(data.folder_id || '');
      } catch (error) {
        if (!mounted) return;
        setEditError(error.message || 'Unable to load prompt.');
      } finally {
        if (mounted) {
          setLoadingPrompt(false);
        }
      }
    };

    loadPrompt();
    return () => {
      mounted = false;
    };
  }, [isEdit, promptId, user?.user_id]);

  useEffect(() => {
    if (selectedProjectId) {
      fetchFolders(selectedProjectId).then(() => {
        if (pendingFolderRef.current && pendingFolderRef.current.projectId === selectedProjectId) {
          setSelectedFolderId(pendingFolderRef.current.folderId || '');
          pendingFolderRef.current = null;
        }
      });
    } else {
      setFolders([]);
      if (!pendingFolderRef.current) {
        setSelectedFolderId('');
      }
    }
  }, [selectedProjectId]);

  useEffect(() => {
    const pattern = /\{\{([^}]+)\}\}/g;
    const matches = [...template.matchAll(pattern)];
    const detected = [...new Set(matches.map((m) => m[1]))];

    setVariables((prev) => {
      const prevMap = new Map(prev.map((v) => [v.variable_name, v]));
      return detected.map((varName) => {
        const existing = prevMap.get(varName);
        return {
          variable_name: varName,
          display_name: existing?.display_name || formatDisplayName(varName),
          description: existing?.description || '',
          input_type: existing?.input_type || 'text',
          is_required: existing?.is_required ?? true,
          default_value: existing?.default_value || '',
          dropdown_options: existing?.dropdown_options || []
        };
      });
    });
  }, [template]);

  const fetchCategories = async () => {
    try {
      const data = await apiClient.get('/api/categories');
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const data = await apiClient.get('/api/projects');
      setProjects(data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };

  const fetchFolders = async (projectIdValue) => {
    try {
      const data = await apiClient.get('/api/folders', { project_id: projectIdValue });
      setFolders(data);
      return data;
    } catch (error) {
      console.error('Failed to fetch folders:', error);
    }
    return [];
  };

  const openProjectModal = () => {
    setProjectForm({ name: '', description: '', category_id: '', color: '#3B82F6', visibility: 'private' });
    setProjectModalOpen(true);
  };

  const openFolderModal = () => {
    setFolderForm((prev) => ({
      ...prev,
      name: '',
      description: '',
      category_id: '',
      color: '#3B82F6',
      visibility: 'private',
      project_id: selectedProjectId || prev.project_id || ''
    }));
    setFolderModalOpen(true);
  };

  const handleCreateProjectInline = async () => {
    if (!projectForm.name.trim()) return;
    if (!projectForm.category_id) {
      alert('Select a category first.');
      return;
    }
    setCreatingProject(true);
    try {
      const saved = await apiClient.post('/api/projects', {
        name: projectForm.name,
        description: projectForm.description,
        category_id: projectForm.category_id || null,
        color: projectForm.color,
        visibility: projectForm.visibility
      });
      setProjects((prev) => [saved, ...prev]);
      setSelectedProjectId(saved.project_id);
      setFieldErrors((prev) => ({ ...prev, project: false }));
      setProjectModalOpen(false);
    } catch (error) {
      console.error('Failed to create project:', error);
      alert('Unable to create project.');
    } finally {
      setCreatingProject(false);
    }
  };

  const handleCreateFolderInline = async () => {
    if (!folderForm.name.trim()) return;
    if (!folderForm.category_id) {
      alert('Select a category first.');
      return;
    }
    if (!folderForm.project_id) {
      alert('Select a project first.');
      return;
    }
    setCreatingFolder(true);
    try {
      const saved = await apiClient.post('/api/folders', {
        name: folderForm.name,
        description: folderForm.description,
        category_id: folderForm.category_id || null,
        project_id: folderForm.project_id,
        color: folderForm.color,
        visibility: folderForm.visibility
      });
      if (folderForm.project_id === selectedProjectId) {
        setFolders((prev) => [saved, ...prev]);
        setSelectedFolderId(saved.folder_id);
      } else {
        setSelectedProjectId(saved.project_id);
        setSelectedFolderId(saved.folder_id);
      }
      setFieldErrors((prev) => ({ ...prev, folder: false, project: false }));
      setFolderModalOpen(false);
    } catch (error) {
      console.error('Failed to create folder:', error);
      alert('Unable to create folder.');
    } finally {
      setCreatingFolder(false);
    }
  };

  const categoryDescriptions = {
    'Analysis': 'Research, summaries, and insights',
    'Automations': 'Agents, workflows, and task orchestration',
    'Business': 'Strategy, ops, and decision memos',
    'Coding': 'Debugging, refactors, and architecture',
    'Creative Writing': 'Stories, characters, and worldbuilding',
    'Customer Support': 'Macros, resolution flows, and tone',
    'Image Generation': 'Visual prompts, styles, and concepts',
    'Marketing': 'Campaigns, ads, and growth experiments',
    'Product': 'Specs, UX writing, and feature strategy',
    'Video Generation': 'Scripts, shots, and storyboards',
    'Other': 'Miscellaneous or custom use cases'
  };

  const handleAddVariable = React.useCallback(() => {
    const textarea = templateRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart ?? template.length;
    const end = textarea.selectionEnd ?? template.length;
    const before = template.slice(0, start);
    const after = template.slice(end);
    const placeholder = 'variable_name';
    const nextValue = `${before}{{${placeholder}}}${after}`;
    setTemplate(nextValue);
    requestAnimationFrame(() => {
      const selectionStart = start + 2;
      const selectionEnd = selectionStart + placeholder.length;
      textarea.focus();
      textarea.setSelectionRange(selectionStart, selectionEnd);
    });
  }, [template]);

  useEffect(() => {
    const handleHotkey = (event) => {
      if (event.defaultPrevented) return;
      if (step !== 1) return;
      if (event.altKey && !event.ctrlKey && !event.metaKey && event.key.toLowerCase() === 'v') {
        event.preventDefault();
        handleAddVariable();
      }
    };

    window.addEventListener('keydown', handleHotkey);
    return () => window.removeEventListener('keydown', handleHotkey);
  }, [handleAddVariable, step]);

  const normalizeTemplateSpaces = (value) => {
    return value.replace(/\{\{([^}]+)\}\}/g, (match, inner) => {
      const cleaned = inner.replace(/\s+/g, '_');
      return `{{${cleaned}}}`;
    });
  };

  const handleTemplateChange = (event) => {
    const nextValue = event.target.value;
    const selectionStart = event.target.selectionStart ?? nextValue.length;
    const selectionEnd = event.target.selectionEnd ?? nextValue.length;
    const normalized = normalizeTemplateSpaces(nextValue);
    if (lockedHighlight) {
      setLockedHighlight(null);
      setHighlightedVariable(null);
    }
    if (normalized !== nextValue) {
      setTemplate(normalized);
      requestAnimationFrame(() => {
        const textarea = templateRef.current;
        if (!textarea) return;
        const safeStart = Math.min(selectionStart, normalized.length);
        const safeEnd = Math.min(selectionEnd, normalized.length);
        textarea.setSelectionRange(safeStart, safeEnd);
      });
    } else {
      setTemplate(nextValue);
    }
  };

  const highlightVariableInTemplate = (variableName, options = {}) => {
    setHighlightedVariable(variableName);
    const textarea = templateRef.current;
    if (!textarea) return;
    const target = `{{${variableName}}}`;
    const index = template.indexOf(target);
    if (index === -1) return;
    if (options.lock) {
      setLockedHighlight(variableName);
    }
    if (!options.forceFocus && document.activeElement !== textarea) {
      return;
    }
    if (options.forceFocus) {
      textarea.focus();
    }
    selectionRef.current = {
      start: textarea.selectionStart ?? 0,
      end: textarea.selectionEnd ?? 0
    };
    const start = index + 2;
    const end = start + variableName.length;
    textarea.setSelectionRange(start, end);
    if (options.scroll) {
      const before = template.slice(0, index);
      const lineCount = before.split('\n').length - 1;
      const style = window.getComputedStyle(textarea);
      const fontSize = parseFloat(style.fontSize || '16');
      const lineHeightRaw = style.lineHeight;
      const lineHeight = Number.isNaN(parseFloat(lineHeightRaw))
        ? Math.round(fontSize * 1.5)
        : parseFloat(lineHeightRaw);
      const paddingTop = parseFloat(style.paddingTop || '0');
      const targetTop = lineCount * lineHeight;
      textarea.scrollTop = Math.max(0, targetTop - paddingTop - lineHeight);
    }
  };

  const clearVariableHighlight = () => {
    if (lockedHighlight) return;
    const textarea = templateRef.current;
    setHighlightedVariable(null);
    if (!textarea) return;
    if (document.activeElement !== textarea) return;
    if (selectionRef.current.start !== null) {
      textarea.setSelectionRange(selectionRef.current.start, selectionRef.current.end);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isEdit && promptOwnerId && user?.user_id && promptOwnerId !== user.user_id) {
      setEditError('You can only edit prompts that you created.');
      return;
    }
    if (step === 1) {
      const missingName = !name.trim();
      const missingTemplate = !template.trim();
      const missingProject = !selectedProjectId;
      const missingFolder = !selectedFolderId;
      const missingCategory = !categoryId;

      if (missingName || missingTemplate || missingProject || missingFolder || missingCategory) {
        setFieldErrors({
          project: missingProject,
          folder: missingFolder,
          category: missingCategory
        });
        setStepError(
          'Please add a prompt name, template, category, project, and folder before continuing.'
        );
        return;
      }
      setStepError('');
      setFieldErrors({ project: false, folder: false, category: false });

      const pattern = /\{\{([^}]+)\}\}/g;
      const normalizedTemplate = template.replace(pattern, (match, inner) => {
        const normalized = normalizeVariableName(inner);
        return normalized ? `{{${normalized}}}` : match;
      });
      if (normalizedTemplate !== template) {
        setTemplate(normalizedTemplate);
      }

      setStep(2);
      return;
    }

    setLoading(true);

    try {
      const sanitizedVariables = variables.map((v) => {
        const needsOptions = v.input_type === 'dropdown' || v.input_type === 'multiple_choice';
        return {
          ...v,
          dropdown_options: needsOptions
            ? (v.dropdown_options || []).map((opt) => opt.trim()).filter(Boolean)
            : null
        };
      });

      const safeDescription = sanitizeRichText(description);
      const payload = isEdit
        ? {
            name,
            description: safeDescription,
            prompt_template: template,
            category_id: categoryId || null,
            project_id: selectedProjectId || null,
            folder_id: selectedFolderId || null,
            visibility,
            forked_from_prompt_id: forkedFromPromptId || null,
            variables: sanitizedVariables
          }
        : {
            name,
            description: safeDescription,
            prompt_template: template,
            category_id: categoryId || null,
            project_id: selectedProjectId || null,
            folder_id: selectedFolderId || null,
            visibility,
            forked_from_prompt_id: forkedFromPromptId || null,
            variables: sanitizedVariables
          };
      if (isEdit) {
        await apiClient.patch(`/api/prompts/${promptId}`, payload);
        navigate(`/prompts/${promptId}`);
      } else {
        const created = await apiClient.post('/api/prompts', payload);
        navigate(`/prompts/${created.prompt_id}`);
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      alert(isEdit ? 'Error updating prompt' : 'Error creating prompt');
    } finally {
      setLoading(false);
    }
  };

  if (loadingPrompt) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (isEdit && editError) {
    return (
      <div className="min-h-screen p-6">
        <div className="card-glass max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-2">Edit Prompt</h2>
          <p className="text-gray-400 mb-6">{editError}</p>
          <button className="btn-primary" onClick={() => navigate(`/prompts/${promptId}`)}>
            Back to Prompt
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen app-page-shell">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <button
            onClick={() => navigate(isEdit ? `/prompts/${promptId}` : '/profile')}
            className="text-gray-400 hover:text-white mb-4"
          >
            {'<-'} {isEdit ? 'Back to Prompt' : 'Back to Profile'}
          </button>
          <h1 className="text-3xl font-bold">
            {isEdit ? 'Edit Prompt' : (forkInfo?.prompt ? 'Create a Fork' : 'Create New Prompt')}
          </h1>
          <p className="text-sm text-gray-400 mt-2">Step {step} of 2</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {forkInfo?.prompt && !isEdit && (
            <div className="card-glass fork-banner">
              <div className="text-xs text-gray-400">Create a fork</div>
              <div className="text-lg font-semibold">
                Forking from{' '}
                <Link className="text-rebel-blue hover:underline" to={`/prompts/${forkInfo.prompt.prompt_id}`}>
                  {forkInfo.prompt.name}
                </Link>
              </div>
              {forkInfo.creator && (
                <div className="text-xs text-gray-500 mt-1">
                  Created by {forkInfo.creator.username || forkInfo.creator.name || 'Rebel Creator'}
                </div>
              )}
            </div>
          )}
          {step === 1 && (
            <>
              <div className="card-glass">
                <h2 className="text-xl font-bold mb-4">Destination</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Project</label>
                    <SelectMenu
                      value={selectedProjectId}
                      options={[
                        { value: '__create_project__', label: 'Create Project', description: 'Add a new project now.' },
                        ...projects.map((project) => ({
                          value: project.project_id,
                          label: project.name,
                          description: project.description || 'Project'
                        }))
                      ]}
                      placeholder="Select a project"
                      className={fieldErrors.project ? 'select-invalid' : ''}
                      onChange={(value) => {
                        if (value === '__create_project__') {
                          openProjectModal();
                          return;
                        }
                        pendingFolderRef.current = null;
                        setSelectedProjectId(value);
                        setSelectedFolderId('');
                        setFieldErrors((prev) => ({ ...prev, project: false }));
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Folder</label>
                    <SelectMenu
                      value={selectedFolderId}
                      options={[
                        { value: '__create_folder__', label: 'Create Folder', description: 'Add a new folder now.' },
                        ...folders.map((folder) => ({
                          value: folder.folder_id,
                          label: folder.name,
                          description: folder.description || 'Folder'
                        }))
                      ]}
                      placeholder={selectedProjectId ? 'Select a folder' : 'Select a project first'}
                      className={fieldErrors.folder ? 'select-invalid' : ''}
                      onChange={(value) => {
                        if (value === '__create_folder__') {
                          openFolderModal();
                          return;
                        }
                        setSelectedFolderId(value);
                        setFieldErrors((prev) => ({ ...prev, folder: false }));
                      }}
                    />
                  </div>
                </div>
                {(projectLabel || folderLabel) && (
                  <p className="text-xs text-gray-400 mt-3">
                    Preselected from your current folder context.
                  </p>
                )}
              </div>
              <div className="card-glass">
                <h2 className="text-xl font-bold mb-4">Basic Information</h2>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Prompt Name *</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    className="input-glass"
                    placeholder="e.g., Blog Post Generator"
                    required
                    data-testid="prompt-name-input"
                  />
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <RichTextInput
                    value={description}
                    onChange={setDescription}
                    placeholder="What does this prompt do?"
                    data-testid="prompt-description-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Category</label>
                  <SelectMenu
                    value={categoryId}
                    options={sortedCategories.map((cat) => ({
                      value: cat.category_id,
                      label: cat.name,
                      description: categoryDescriptions[cat.name] || 'AI prompt topic'
                    }))}
                    placeholder="Select a category"
                    className={fieldErrors.category ? 'select-invalid' : ''}
                    onChange={(value) => {
                      setCategoryId(value);
                      setFieldErrors((prev) => ({ ...prev, category: false }));
                    }}
                    data-testid="prompt-category-select"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2">Visibility</label>
                  <SelectMenu
                    value={visibility}
                    options={[
                      { value: 'private', label: 'Private', description: 'Only you can access this prompt.' },
                      { value: 'public', label: 'Public', description: 'Anyone can view and use this prompt.' }
                    ]}
                    placeholder="Select visibility"
                    onChange={setVisibility}
                  />
                </div>
              </div>

              <div className="card-glass">
                <h2 className="text-xl font-bold mb-4">Prompt Template</h2>
                <p className="text-sm text-gray-400 mb-4">
                  Use {'{{variable_name}}'} to create variables that users can fill in
                </p>
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs text-gray-500">
                    <div>Add variables quickly</div>
                    <div className="text-gray-400">Variables become fillable inputs in the next step.</div>
                    <div className="text-gray-500">Hotkeys: Alt + V (Windows/Linux), Option + V (Mac)</div>
                  </div>
                  <button
                    type="button"
                    className="btn-secondary btn-compact btn-no-shadow"
                    onClick={handleAddVariable}
                  >
                    Add Variable
                  </button>
                </div>

                <textarea
                  value={template}
                  onChange={handleTemplateChange}
                  ref={templateRef}
                  onBlur={() => {
                    setLockedHighlight(null);
                    setHighlightedVariable(null);
                  }}
                  className={`input-glass font-mono ${highlightedVariable ? 'template-highlight' : ''}`}
                  rows="10"
                  placeholder="Write a blog post about {{topic}} targeting {{audience}}..."
                  required
                  data-testid="prompt-template-input"
                />

                {variables.length > 0 && (
                  <div className="mt-4 p-4 rounded-lg bg-white/5">
                    <h3 className="font-bold mb-2">Detected Variables ({variables.length})</h3>
                    <div className="space-y-2">
                      {variables.map((v, index) => (
                        <div
                          key={`${v.variable_name}-${index}`}
                          className={`text-sm variable-item ${highlightedVariable === v.variable_name ? 'active' : ''}`}
                          onMouseEnter={() => {
                            if (!lockedHighlight || lockedHighlight === v.variable_name) {
                              highlightVariableInTemplate(v.variable_name);
                            }
                          }}
                          onMouseLeave={clearVariableHighlight}
                          onClick={() => highlightVariableInTemplate(v.variable_name, { forceFocus: true, lock: true, scroll: true })}
                        >
                          <span className="text-rebel-blue">{`{{${v.variable_name}}}`}</span>
                          <span className="text-gray-400 ml-2">-&gt; {v.display_name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {step === 2 && (
            <div className="card-glass">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold">Variable Input Settings</h2>
                  <p className="text-sm text-gray-400 mt-1">
                    Configure how users will fill in each variable.
                  </p>
                </div>
                <span className="badge">{variables.length} variables</span>
              </div>

              {variables.length === 0 ? (
                <div className="p-4 rounded-lg bg-white/5 text-sm text-gray-400">
                  No variables detected. You can go back and add variables in your template.
                </div>
              ) : (
                <div className="space-y-6">
                  {variables.map((variable, index) => {
                    const needsOptions = variable.input_type === 'dropdown' || variable.input_type === 'multiple_choice';
                    return (
                      <div key={variable.variable_name} className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <div className="flex items-center justify-between mb-4">
                          <div className="text-sm">
                            <span className="text-rebel-blue">{`{{${variable.variable_name}}}`}</span>
                          </div>
                          <span className="text-xs uppercase tracking-wide text-gray-400">Input {index + 1}</span>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Input Name</label>
                            <input
                              type="text"
                              value={variable.display_name}
                              onChange={(event) => {
                                const value = event.target.value;
                                setVariables((prev) => prev.map((v, i) => (i === index ? { ...v, display_name: value } : v)));
                              }}
                              className="input-glass"
                              placeholder="Variable Input"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-2">Input Type</label>
                            <SelectMenu
                              value={variable.input_type}
                              options={[
                                { value: 'text', label: 'Text' },
                                { value: 'multi_text', label: 'Multi-Text' },
                                { value: 'dropdown', label: 'Dropdown' },
                                { value: 'multiple_choice', label: 'Multiple Choice' },
                                { value: 'number', label: 'Number' }
                              ]}
                              placeholder="Select input type"
                              onChange={(value) => {
                                setVariables((prev) =>
                                  prev.map((v, i) => (i === index ? { ...v, input_type: value } : v))
                                );
                              }}
                            />
                          </div>
                        </div>

                        <div className="mt-4">
                          <label className="block text-sm font-medium mb-2">Description</label>
                          <textarea
                            value={variable.description}
                            onChange={(event) => {
                              const value = event.target.value;
                              setVariables((prev) => prev.map((v, i) => (i === index ? { ...v, description: value } : v)));
                            }}
                            className="input-glass"
                            rows="2"
                            placeholder="Explain how this variable should be used"
                          />
                        </div>

                        {needsOptions && (
                          <div className="mt-4">
                            <label className="block text-sm font-medium mb-2">Choices</label>
                            <div className="space-y-2">
                              {(variable.dropdown_options || []).map((opt, optIndex) => (
                                <div key={`${variable.variable_name}-${optIndex}`} className="flex gap-2">
                                  <input
                                    type="text"
                                    value={opt}
                                    onChange={(event) => {
                                      const value = event.target.value;
                                      setVariables((prev) => prev.map((v, i) => {
                                        if (i !== index) return v;
                                        const nextOptions = [...(v.dropdown_options || [])];
                                        nextOptions[optIndex] = value;
                                        return { ...v, dropdown_options: nextOptions };
                                      }));
                                    }}
                                    className="input-glass flex-1"
                                    placeholder={`Option ${optIndex + 1}`}
                                  />
                                  <button
                                    type="button"
                                    className="btn-secondary btn-compact"
                                    onClick={() => {
                                      setVariables((prev) => prev.map((v, i) => {
                                        if (i !== index) return v;
                                        const nextOptions = [...(v.dropdown_options || [])];
                                        nextOptions.splice(optIndex, 1);
                                        return { ...v, dropdown_options: nextOptions };
                                      }));
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                className="btn-secondary btn-compact"
                                onClick={() => {
                                  setVariables((prev) => prev.map((v, i) => {
                                    if (i !== index) return v;
                                    const nextOptions = [...(v.dropdown_options || [])];
                                    nextOptions.push('');
                                    return { ...v, dropdown_options: nextOptions };
                                  }));
                                }}
                              >
                                Add Option
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {stepError && (
            <div className="text-sm text-red-400">{stepError}</div>
          )}

          <div className="flex gap-4">
            {step === 2 && (
              <button
                type="button"
                onClick={() => setStep(1)}
                className="btn-secondary"
              >
                Back
              </button>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1"
              data-testid="create-prompt-submit"
            >
              {step === 1
                ? 'Next: Configure Inputs'
                : (loading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Prompt'))}
            </button>
            <button
              type="button"
              onClick={() => navigate(isEdit ? `/prompts/${promptId}` : '/profile')}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>

        <Modal
          open={projectModalOpen}
          title="Create Project"
          onClose={() => setProjectModalOpen(false)}
          footer={(
            <>
              <button className="btn-secondary btn-compact" onClick={() => setProjectModalOpen(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreateProjectInline} disabled={creatingProject}>
                {creatingProject ? 'Creating...' : 'Create Project'}
              </button>
            </>
          )}
        >
          <div className="modal-field">
            <label>Name</label>
            <input
              className="input-glass"
              value={projectForm.name}
              onChange={(e) => setProjectForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Project name"
            />
          </div>
        <div className="modal-field">
          <label>Description</label>
          <textarea
            className="input-glass"
            rows="3"
            value={projectForm.description}
            onChange={(e) => setProjectForm((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Optional description"
          />
        </div>
        <div className="modal-field">
          <label>Category</label>
          <SelectMenu
            value={projectForm.category_id}
            options={sortedCategories.map((category) => ({
              value: category.category_id,
              label: category.name
            }))}
            placeholder="Select a category"
            onChange={(value) => setProjectForm((prev) => ({ ...prev, category_id: value }))}
          />
        </div>
        <div className="modal-field">
          <label>Accent Color</label>
          <ColorPicker value={projectForm.color} onChange={(color) => setProjectForm((prev) => ({ ...prev, color }))} />
        </div>
          <div className="modal-field">
            <label>Visibility</label>
            <SelectMenu
              value={projectForm.visibility}
              options={[
                { value: 'private', label: 'Private', description: 'Only you can access this project.' },
                { value: 'public', label: 'Public', description: 'Anyone can view this project.' }
              ]}
              placeholder="Select visibility"
              onChange={(value) => setProjectForm((prev) => ({ ...prev, visibility: value }))}
            />
          </div>
        </Modal>

        <Modal
          open={folderModalOpen}
          title="Create Folder"
          onClose={() => setFolderModalOpen(false)}
          footer={(
            <>
              <button className="btn-secondary btn-compact" onClick={() => setFolderModalOpen(false)}>
                Cancel
              </button>
              <button className="btn-primary" onClick={handleCreateFolderInline} disabled={creatingFolder}>
                {creatingFolder ? 'Creating...' : 'Create Folder'}
              </button>
            </>
          )}
        >
          <div className="modal-field">
            <label>Project</label>
            <SelectMenu
              value={folderForm.project_id}
              options={projects.map((project) => ({
                value: project.project_id,
                label: project.name,
                description: project.description || 'Project'
              }))}
              placeholder="Select a project"
              onChange={(value) => setFolderForm((prev) => ({ ...prev, project_id: value }))}
            />
          </div>
          <div className="modal-field">
            <label>Name</label>
            <input
              className="input-glass"
              value={folderForm.name}
              onChange={(e) => setFolderForm((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Folder name"
            />
          </div>
        <div className="modal-field">
          <label>Description</label>
          <textarea
            className="input-glass"
            rows="3"
            value={folderForm.description}
            onChange={(e) => setFolderForm((prev) => ({ ...prev, description: e.target.value }))}
            placeholder="Optional description"
          />
        </div>
        <div className="modal-field">
          <label>Category</label>
          <SelectMenu
            value={folderForm.category_id}
            options={sortedCategories.map((category) => ({
              value: category.category_id,
              label: category.name
            }))}
            placeholder="Select a category"
            onChange={(value) => setFolderForm((prev) => ({ ...prev, category_id: value }))}
          />
        </div>
        <div className="modal-field">
          <label>Accent Color</label>
          <ColorPicker value={folderForm.color} onChange={(color) => setFolderForm((prev) => ({ ...prev, color }))} />
        </div>
          <div className="modal-field">
            <label>Visibility</label>
            <SelectMenu
              value={folderForm.visibility}
              options={[
                { value: 'private', label: 'Private', description: 'Only you can access this folder.' },
                { value: 'public', label: 'Public', description: 'Anyone can view this folder.' }
              ]}
              placeholder="Select visibility"
              onChange={(value) => setFolderForm((prev) => ({ ...prev, visibility: value }))}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { setUser } = React.useContext(AuthContext);

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/signup" element={<ExternalRedirectPage href={SKOOL_SIGNUP_URL} />} />
      <Route path="/signup/invite/:token" element={<InviteSignupPage setUser={setUser} />} />
      <Route path="/login" element={<LoginPage setUser={setUser} />} />
      <Route path="/welcome" element={<ProtectedRoute><WelcomePage /></ProtectedRoute>} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/docs" element={<Documentation />} />
      <Route path="/support" element={<Support />} />
      <Route path="/invite/:token" element={<InviteAcceptPage />} />
      <Route path="/social" element={<SocialHubHome />} />
      <Route path="/social/communities" element={<SocialGuildDirectory />} />
      <Route path="/social/communities/:guildId" element={<SocialGuildPage />} />
      <Route path="/social/guilds" element={<Navigate to="/social/communities" replace />} />
      <Route path="/social/guilds/:guildId" element={<SocialGuildPage />} />
      <Route path="/social/posts/:postId" element={<SocialPostDetail />} />
      <Route path="/social/notifications" element={<ProtectedRoute><SocialNotifications /></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><InboxPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<Navigate to="/profile" replace />} />
      <Route path="/projects" element={<ProtectedRoute><ProjectsIndexView /></ProtectedRoute>} />
      <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectFoldersView /></ProtectedRoute>} />
      <Route path="/projects/:projectId/folders/:folderId" element={<ProtectedRoute><FolderPromptsView /></ProtectedRoute>} />
      <Route path="/prompts/create" element={<ProtectedRoute><CreatePrompt /></ProtectedRoute>} />
      <Route path="/prompts/:promptId/edit" element={<ProtectedRoute><CreatePrompt /></ProtectedRoute>} />
      <Route path="/prompts/:promptId" element={<ProtectedRoute><PromptDetail /></ProtectedRoute>} />
                        <Route path="/agent-instructions" element={<ProtectedRoute><InstructionProjectsIndexView /></ProtectedRoute>} />
                        <Route path="/agent-instructions/projects/:projectId" element={<ProtectedRoute><InstructionProjectFoldersView /></ProtectedRoute>} />
                        <Route path="/agent-instructions/projects/:projectId/folders/:folderId" element={<ProtectedRoute><InstructionFolderInstructionsView /></ProtectedRoute>} />
                        <Route path="/agent-instructions/create" element={<ProtectedRoute><AgentInstructionsPage /></ProtectedRoute>} />
                        <Route path="/agent-instructions/:instructionId" element={<ProtectedRoute><AgentInstructionsPage /></ProtectedRoute>} />
                        <Route path="/agent-instructions/:instructionId/edit" element={<ProtectedRoute><AgentInstructionsPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/profile/:userId" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/shared" element={<ProtectedRoute><SharedPage /></ProtectedRoute>} />
      <Route path="/saved" element={<ProtectedRoute><SavedPromptsPage /></ProtectedRoute>} />
      <Route path="/favorites" element={<ProtectedRoute><FavoritePromptsPage /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><PromptLibraryPage /></ProtectedRoute>} />
      <Route path="/library/instructions/:instructionId" element={<ProtectedRoute><PublicInstructionPage /></ProtectedRoute>} />
      <Route path="/trash" element={<ProtectedRoute><TrashPage /></ProtectedRoute>} />
      <Route path="/courses" element={<ProtectedRoute><CoursesPage /></ProtectedRoute>} />
      <Route path="/courses/new" element={<ProtectedRoute><CoursesPage /></ProtectedRoute>} />
      <Route path="/courses/:courseId" element={<ProtectedRoute><CoursesPage /></ProtectedRoute>} />
      <Route path="/courses/:courseId/edit" element={<ProtectedRoute><CoursesPage /></ProtectedRoute>} />
      <Route path="/courses/:courseId/builder" element={<ProtectedRoute><CoursesPage /></ProtectedRoute>} />
      <Route path="/courses/:courseId/learn" element={<ProtectedRoute><CoursesPage /></ProtectedRoute>} />
      <Route path="/api" element={<ProtectedRoute><ApiPage /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
      <Route path="/team" element={<ProtectedRoute><TeamPage /></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <ScrollToTop />
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
